import React, { useState } from 'react'
import { t } from '../../../i18n/i18n'
import { DataClassification, DemandAnalysis, SubscriptionLevel } from '../../../api/schemas/schema'
import { Grid } from '../../common/grid/Grid'
import { GridItem } from '../../common/grid/GridItem'
import { Text } from '../../common/text/Text'
import { Persona, makeStyles, tokens } from '@fluentui/react-components'
import { translateEnumValue } from '../../../helpers/enumHelper'
import { getFullName } from '../../../helpers/accountHelper'
import { InformationCard } from '../../common/card/InformationCard'
import { ModalEditDemandInformation } from './ModalEditDemandInformation'
import { IItemState } from '../../../interfaces/IItemState'
import { formatDate } from '../../../helpers/stringHelper'
import TextOutput from '../../common/inputs/TextOutput'

const useStyles = makeStyles({
   infoWrapper: {
      '& > p': {
         marginTop: '0.5em',
         marginBottom: '0.5em',
      },
      '& > :last-child': {
         marginBottom: '1.5em',
         '& > span:last-child': {
            fontSize: tokens.fontSizeBase400,
         },
      },
   },
   editButton: {
      display: 'flex',
      alignItems: 'center',
   },
   flexRow: {
      display: 'flex',
      gap: tokens.spacingHorizontalXXXL,
   },
})

type DemandAssesmentInformationProps = {
   analysisState: IItemState<DemandAnalysis>
   setAnalysisState: React.Dispatch<React.SetStateAction<IItemState<DemandAnalysis>>>
}

export const DemandAssesmentInformation = ({ analysisState, setAnalysisState }: DemandAssesmentInformationProps) => {
   const classes = useStyles()
   const [isModalOpen, setIsModalOpen] = useState(false)
   const analysis = analysisState.item

   const handleEditButtonClick = () => {
      setIsModalOpen(true)
   }

   const closeModal = () => {
      setIsModalOpen(false)
   }

   return (
      <>
         <InformationCard setState={setAnalysisState} handleEditButtonClick={handleEditButtonClick} disabled={analysisState.disabled}>
            <Grid>
               <GridItem size="4/12">
                  <section className={classes.infoWrapper}>
                     <Text weight="bold">{t('StartOfAssessment')}</Text>
                     {analysis.analysisStart ? <Text>{formatDate(analysis.analysisStart)}</Text> : <Text italic>{t('NoDateSet')}</Text>}
                  </section>
                  <section className={classes.infoWrapper}>
                     <Text weight="bold">{t('EndOfAssessment')}</Text>
                     {analysis.analysisEnd ? <Text>{formatDate(analysis.analysisEnd)}</Text> : <Text italic>{t('NoDateSet')}</Text>}
                  </section>
                  <section className={classes.infoWrapper}>
                     <Text weight="bold">{t('Owner')}</Text>
                     <Persona textAlignment="center" name={getFullName(analysis.analysisOwner)} primaryText={getFullName(analysis.analysisOwner)} />
                  </section>
                  <section className={classes.infoWrapper}>
                     <TextOutput label={t('DemandLevel')} labelWeight="bold" text={translateEnumValue(SubscriptionLevel, analysis.demandLevel)} />
                  </section>
                  <section className={classes.infoWrapper}>
                     <TextOutput label={t('Workplace')} labelWeight="bold" text={analysis.workplace.name} />
                  </section>
                  <section className={classes.infoWrapper}>
                     <Text weight="bold">{t('InformationManagement')}</Text>
                     {analysis.dataClassification !== undefined ? (
                        <Text>{translateEnumValue(DataClassification, analysis.dataClassification)}</Text>
                     ) : (
                        <Text italic>{t('NoValueSet')}</Text>
                     )}
                  </section>
                  <section className={classes.infoWrapper}>
                     <TextOutput label={t('Comment')} labelWeight="bold" text={analysis.comment} />
                  </section>
               </GridItem>
               {/* demandAnalysisProcessOwners is empty when the analysis is created by a sandbox org */}
               {analysis.processes.length > 0 && analysis.demandAnalysisProcessOwners.length > 0 && (
                  <GridItem size="6/12">
                     <section className={classes.infoWrapper}>
                        <div className={classes.flexRow}>
                           <div>
                              <Text weight="bold">{t('Process')}</Text>
                              {analysis.demandAnalysisProcessOwners.map((p, index) => (
                                 <Text key={`pro-${index}`}>{p.process.text}</Text>
                              ))}
                           </div>

                           <div>
                              <Text weight="bold">{t('Responsible')}</Text>
                              {analysis.demandAnalysisProcessOwners.map((p, index) => (
                                 <Text key={`res-${index}`}>{p.userAccount ? getFullName(p.userAccount) : p?.ownerFreeText}</Text>
                              ))}
                           </div>
                        </div>
                     </section>
                  </GridItem>
               )}
            </Grid>
         </InformationCard>
         <ModalEditDemandInformation analysisState={analysisState} isOpen={isModalOpen} closeModal={closeModal} setAnalysisState={setAnalysisState} />
      </>
   )
}
